// In-match pause / settings trigger. Listens for Esc, gamepad Start, and
// the mobile ⋯ button — opens the same SettingsScreen modal that the
// home menu opens. While paused: input.setReleased(true) freezes the
// character; on close, setReleased(false) re-arms control.
//
// Gating:
//   - Suppressed while a RoundEndModal / GameEndModal is showing (those
//     own their own Esc handling and a stacked pause would conflict).
//   - Suppressed during tutorial cinematics (the user wanted these
//     uninterruptible; pause becomes available again the moment the
//     cinematic ends).
//   - The gamepad poll runs off rAF so it stops when the document
//     becomes hidden or PauseController is disposed.
//
// Single source of truth for the modal — see SettingsScreen.ts. This
// controller wires the entry points; the modal itself is identical
// whether opened from the home or pause flows.

import type { Input } from '../core/Input';
import { showSettingsModal, type SettingsModalHandle } from './SettingsScreen';

export interface PauseControllerOptions {
  input: Input;
  /** Whether this is the tutorial map. Only meaningful for the Skip
   *  Tutorial button visibility. Defaults to false. */
  isTutorial?: boolean;
  /** Active tutorial flow handle (only when isTutorial). Skip Tutorial in
   *  the modal calls requestSkip() and dismisses. */
  tutorialFlow?: { requestSkip: () => void } | null;
  /** Gate: the controller calls this every Esc / Start / menu-tap and
   *  refuses to open the modal when it returns true. Caller wires this
   *  to a check like `() => modals.round !== null || modals.game !== null
   *  || coord.isCinematicActive()`. */
  isBlocked?: () => boolean;
  /** Optional Leave Game hook — surfaced as an orange action in the
   *  Gameplay tab. The local match runner wires this to its
   *  resolveEnd(). */
  onLeaveMatch?: () => void;
}

const START_BUTTON_INDEX = 9;

export class PauseController {
  private modal: SettingsModalHandle | null = null;
  private rafHandle: number | null = null;
  private prevStart = false;
  private prevTouchMenuTap: (() => void) | null = null;
  private started = false;

  constructor(private readonly opts: PauseControllerOptions) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    // Capture-phase Esc — fires before Input's bubble-phase keydown so
    // the existing Esc → setReleased toggle doesn't conflict with our
    // modal trigger. We also stopImmediatePropagation so no other
    // capture-phase listener (e.g. a downstream HUD overlay) double-fires.
    addEventListener('keydown', this.onKeyDown, { capture: true });
    // Mobile ⋯ menu button rewires through Input's TouchControls. Stash
    // any previous handler so dispose() restores it cleanly.
    this.prevTouchMenuTap = this.opts.input.touch.onMenuTap;
    this.opts.input.touch.onMenuTap = () => this.toggle();
    this.tickGamepad();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    removeEventListener('keydown', this.onKeyDown, { capture: true } as EventListenerOptions);
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    this.rafHandle = null;
    this.opts.input.touch.onMenuTap = this.prevTouchMenuTap;
    this.prevTouchMenuTap = null;
    // If the modal is open at teardown, close it — the match is gone, so
    // there's nothing left to resume. dismiss() also runs onClose which
    // would call input.setReleased(false), but Input is on its way to
    // disposal too; the order is harmless.
    if (this.modal) {
      this.modal.dismiss();
      this.modal = null;
    }
  }

  /** Open the modal if not already open, otherwise dismiss it. Mirror of
   *  the user's mental model for the pause button. */
  toggle(): void {
    if (this.modal) {
      this.modal.dismiss();
      return;
    }
    if (this.opts.isBlocked?.()) return;
    this.openPause();
  }

  private openPause(): void {
    if (this.modal) return;
    this.opts.input.setReleased(true);
    this.modal = showSettingsModal({
      context: 'pause',
      isTutorial: this.opts.isTutorial ?? false,
      tutorialFlow: this.opts.tutorialFlow ?? null,
      onLeaveMatch: this.opts.onLeaveMatch,
      onClose: () => {
        this.modal = null;
        // Resume control — Input.setReleased(false) clears the keys/buttons
        // mask so a held key from the modal doesn't immediately fire on
        // resume. Pointer-lock re-acquires when the user clicks the canvas.
        if (this.started) this.opts.input.setReleased(false);
      }
    });
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // P is the primary pause keybind on KBM. Escape and Backspace are kept as
    // fallbacks for legacy muscle memory, but browsers tend to swallow Esc
    // (exits fullscreen / releases pointer-lock) before we ever see it.
    if (e.code !== 'KeyP' && e.code !== 'Escape' && e.code !== 'Backspace') return;
    if (e.code === 'KeyP') {
      const tgt = e.target as HTMLElement | null;
      const tag = tgt?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tgt?.isContentEditable) return;
    }
    if (this.modal) return; // modal's own Esc-handler dismisses
    if (this.opts.isBlocked?.()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.openPause();
  };

  private tickGamepad = (): void => {
    if (!this.started) return;
    this.rafHandle = requestAnimationFrame(this.tickGamepad);
    const pads = navigator.getGamepads?.() ?? [];
    let startDown = false;
    for (const p of pads) {
      if (!p || !p.connected) continue;
      const b = p.buttons[START_BUTTON_INDEX];
      if (b && (b.pressed || b.value > 0.5)) { startDown = true; break; }
    }
    if (startDown && !this.prevStart) {
      // Edge: open / close on each Start press. While the modal is open
      // the user already has Esc / Cancel-via-A — toggling on Start is
      // the same affordance and feels natural on a controller.
      this.toggle();
    }
    this.prevStart = startDown;
  };
}
